/**
 * Validace formuláře generování (fáze 2). Čisté funkce — vrací chybovou hlášku,
 * nebo null když je hodnota v pořádku. Stejná pravidla zopakuje BE ve fázi 7.
 */

/** Normalizace RČ: pryč mezery a lomítko. Ukládá se vždy normalizovaný tvar. */
export function normalizePersonalNumber(value: string): string {
  return value.replace(/[\s/]/g, '')
}

/**
 * České RČ: 9 číslic (narození před 1954, bez kontrolní číslice) nebo 10 číslic
 * (prvních 9 mod 11 = kontrolní číslice; zbytek 10 → historická výjimka s 0).
 * Ženy mají měsíc +50; od 2004 existuje i +20/+70 při vyčerpání řad.
 */
export function validatePersonalNumber(raw: string): string | null {
  const rc = normalizePersonalNumber(raw)
  if (!/^\d{9,10}$/.test(rc)) {
    return 'RČ musí mít 9–10 číslic (lomítko se ignoruje)'
  }

  const year = Number(rc.slice(0, 2))
  let month = Number(rc.slice(2, 4))
  const day = Number(rc.slice(4, 6))

  if (rc.length === 9 && year > 53) {
    return 'Devítimístné RČ platí jen pro narozené před rokem 1954'
  }
  if (month > 70 && rc.length === 10) month -= 70
  else if (month > 50) month -= 50
  else if (month > 20 && rc.length === 10) month -= 20
  if (month < 1 || month > 12) {
    return 'RČ má neplatný měsíc'
  }
  if (day < 1 || day > 31) {
    return 'RČ má neplatný den'
  }

  if (rc.length === 10) {
    const mod = Number(rc.slice(0, 9)) % 11
    const check = Number(rc.slice(9))
    const expected = mod === 10 ? 0 : mod
    if (check !== expected) {
      return 'RČ nesedí na kontrolní číslici (modulo 11)'
    }
  }
  return null
}

/** IČO: 8 číslic, vážený součet (váhy 8..2) mod 11, kontrolní = (11 − mod) mod 10. */
export function validateCompanyId(raw: string): string | null {
  const ico = raw.replace(/\s/g, '')
  if (!/^\d{8}$/.test(ico)) {
    return 'IČO musí mít přesně 8 číslic'
  }
  let sum = 0
  for (let i = 0; i < 7; i++) {
    sum += Number(ico[i]) * (8 - i)
  }
  const expected = (11 - (sum % 11)) % 10
  if (Number(ico[7]) !== expected) {
    return 'IČO nesedí na kontrolní číslici'
  }
  return null
}

export function validateEmail(raw: string): string | null {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(raw.trim())) {
    return 'Neplatný e-mail'
  }
  return null
}

/** Parsuje částku z textu ("1 000 000" i "1000000,50"). Vrací null, když nejde o číslo. */
export function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[\s ]/g, '').replace(',', '.')
  if (!cleaned) return null
  const value = Number(cleaned)
  return Number.isFinite(value) ? value : null
}

export function validateAmount(raw: string): string | null {
  const value = parseAmount(raw)
  if (value === null) {
    return 'Zadejte částku číslem'
  }
  if (value <= 0) {
    return 'Částka musí být větší než 0'
  }
  return null
}

/** Číslo účtu vč. banky: [predčíslí-]číslo/kód banky. */
export function validateAccountNumber(raw: string): string | null {
  if (!/^(?:\d{2,6}-)?\d{2,10}\/\d{4}$/.test(raw.trim())) {
    return 'Číslo účtu zadejte ve tvaru 123456789/0300'
  }
  return null
}
